import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Tooltip from "@material-ui/core/Tooltip";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";
import MenuItem from "@material-ui/core/MenuItem";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleIcon from "@material-ui/icons/People";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "#f8faff"
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2.5)
  },
  title: {
    fontWeight: 800,
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#fff" : "#0A1628"
  },
  addBtn: {
    borderRadius: 12,
    fontWeight: 700,
    fontSize: "0.82rem",
    textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff",
    padding: theme.spacing(0.8, 2),
    "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)" }
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  },
  serviceCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 8px 24px rgba(0,102,255,0.10)" }
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1)
  },
  cardName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "#fff" : "#0A1628"
  },
  cardDesc: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    minHeight: 18
  },
  cardMeta: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5)
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary
  },
  metaIcon: { fontSize: "0.9rem", color: "#0066FF" },
  statusChip: { height: 22, fontSize: "0.68rem", fontWeight: 700, borderRadius: 8 },
  professionalsRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(0.5)
  },
  profAvatar: {
    width: 28,
    height: 28,
    fontSize: "0.75rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    fontWeight: 700
  },
  cardActions: { display: "flex", gap: 4 },
  modalSection: { marginTop: theme.spacing(2) },
  sectionLabel: {
    fontWeight: 700,
    fontSize: "0.82rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#555",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: 0.5
  },
  profCheckCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.15s",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fafafa",
    "&:hover": {
      borderColor: "#0066FF",
      background: theme.palette.type === "dark" ? "rgba(0,102,255,0.08)" : "rgba(0,102,255,0.04)"
    }
  },
  profCheckCardSelected: {
    borderColor: "#0066FF",
    background: theme.palette.type === "dark" ? "rgba(0,102,255,0.12)" : "rgba(0,102,255,0.06)"
  },
  profCheckName: { fontWeight: 600, fontSize: "0.85rem", flex: 1 }
}));

const initialForm = {
  name: "",
  description: "",
  durationMinutes: 30,
  price: "",
  active: true,

  // Configuração comercial do serviço.
  showPrice: true,
  serviceFlowMode: "direct",
  postChoicePrompt: ""
};

const ServicesManager = () => {
  const classes = useStyles();
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingService, setDeletingService] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedProfIds, setSelectedProfIds] = useState([]);
  const [serviceProfMap, setServiceProfMap] = useState({});
  const [catalogByService, setCatalogByService] = useState({});

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await api.get("/services");
      setServices(data);
    } catch (err) { toastError(err); }
  }, []);

  const fetchProfessionals = useCallback(async () => {
    try {
      const { data } = await api.get("/users/list");
      setProfessionals(data || []);
    } catch (err) { toastError(err); }
  }, []);

  const fetchServiceProfessionals = useCallback(async () => {
    try {
      const { data } = await api.get("/service-professionals");
      const map = {};
      data.forEach(sp => {
        if (!map[sp.serviceId]) map[sp.serviceId] = [];
        map[sp.serviceId].push(sp.userId);
      });
      setServiceProfMap(map);
    } catch (err) {}
  }, []);

  const fetchCatalogRules = useCallback(async () => {
    try {
      const { data } =
        await api.get("/catalog-items", {
          params: {
            type: "service"
          }
        });

      const map = {};

      (data || []).forEach(item => {
        if (item.serviceId) {
          map[item.serviceId] = item;
        }
      });

      setCatalogByService(map);
    } catch (err) {
      // Não bloqueia Agenda caso o catálogo esteja indisponível.
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchProfessionals();
    fetchServiceProfessionals();
    fetchCatalogRules();
  }, [
    fetchServices,
    fetchProfessionals,
    fetchServiceProfessionals,
    fetchCatalogRules
  ]);

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);

      const catalog =
        catalogByService[service.id] || {};

      setForm({
        name: service.name,
        description:
          service.description || "",
        durationMinutes:
          service.durationMinutes,
        price:
          service.price || "",
        active:
          service.active !== false,

        showPrice:
          catalog.showPrice !== false &&
          Number(service.price || 0) > 0,

        serviceFlowMode:
          catalog.serviceFlowMode ||
          "direct",

        postChoicePrompt:
          catalog.postChoicePrompt ||
          ""
      });

      setSelectedProfIds(
        serviceProfMap[service.id] || []
      );
    } else {
      setEditingService(null);
      setForm(initialForm);
      setSelectedProfIds([]);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingService(null);
    setForm(initialForm);
    setSelectedProfIds([]);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleProfessional = profId => {
    setSelectedProfIds(prev => prev.includes(profId) ? prev.filter(id => id !== profId) : [...prev, profId]);
  };

  const handleSave = async () => {
    try {
      let serviceId;

      const servicePayload = {
        name: form.name,
        description: form.description,
        durationMinutes:
          form.durationMinutes,
        price:
          form.price || 0,
        active:
          form.active
      };

      if (editingService) {
        await api.put(
          `/services/${editingService.id}`,
          servicePayload
        );

        serviceId =
          editingService.id;

        toast.success(
          "Serviço atualizado"
        );
      } else {
        const { data } =
          await api.post(
            "/services",
            servicePayload
          );

        serviceId = data.id;

        toast.success(
          "Serviço criado"
        );
      }

      try {
        await api.post(
          "/service-professionals/sync",
          {
            serviceId,
            userIds: selectedProfIds
          }
        );
      } catch (err) {}

      /*
       * O Service é sincronizado automaticamente
       * com um CatalogItem.
       *
       * Aqui gravamos apenas a camada comercial.
       */
      try {
        const { data: catalogItems } =
          await api.get(
            "/catalog-items",
            {
              params: {
                type: "service"
              }
            }
          );

        const catalogItem =
          (catalogItems || []).find(
            item =>
              Number(item.serviceId) ===
              Number(serviceId)
          );

        if (catalogItem) {
          await api.put(
            `/catalog-items/${catalogItem.id}`,
            {
              ...catalogItem,

              showPrice:
                Boolean(form.showPrice),

              serviceFlowMode:
                form.serviceFlowMode ||
                "direct",

              postChoiceMode:
                "open_agenda",

              postChoicePrompt:
                form.postChoicePrompt ||
                null
            }
          );
        }
      } catch (err) {
        console.error(
          "[ServicesManager] Falha ao salvar configuração comercial:",
          err
        );

        toast.warning(
          "Serviço salvo, mas não foi possível atualizar a configuração comercial."
        );
      }

      handleCloseModal();

      fetchServices();
      fetchServiceProfessionals();
      fetchCatalogRules();

    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/services/${deletingService.id}`);
      toast.success("Serviço excluído");
    } catch (err) { toastError(err); }
    setDeletingService(null);
    fetchServices();
  };

  const getProfName = id => { const p = professionals.find(p => p.id === id); return p ? p.name : "?"; };
  const getProfInitials = id => getProfName(id).split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase();

  return (
    <Paper className={classes.root} variant="outlined">
      <ConfirmationModal title="Excluir serviço?" open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete}>
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingService ? "Editar serviço" : "Novo serviço"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Nome do serviço" name="name" value={form.name} onChange={handleChange} variant="outlined" fullWidth autoFocus />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descrição" name="description" value={form.description} onChange={handleChange} variant="outlined" fullWidth multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Duração (min)" name="durationMinutes" type="number" value={form.durationMinutes} onChange={handleChange} variant="outlined" fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Valor (R$)" name="price" type="number" value={form.price} onChange={handleChange} variant="outlined" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Divider style={{ margin: "4px 0 8px" }} />

              <Typography
                className={classes.sectionLabel}
                style={{ marginBottom: 10 }}
              >
                Como a IA deve apresentar este serviço
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <Switch
                  checked={Boolean(form.showPrice)}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      showPrice:
                        e.target.checked
                    }))
                  }
                  color="primary"
                  size="small"
                />

                <div>
                  <Typography variant="body2">
                    Mostrar valor ao cliente
                  </Typography>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Desative quando o valor depender de avaliação, diagnóstico ou orçamento.
                  </Typography>
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Como conduzir este serviço?"
                value={form.serviceFlowMode}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    serviceFlowMode:
                      e.target.value
                  }))
                }
                variant="outlined"
                fullWidth
              >
                <MenuItem value="direct">
                  Agendamento direto
                </MenuItem>

                <MenuItem value="evaluation">
                  Avaliação antes do serviço
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={
                  form.serviceFlowMode ===
                  "evaluation"
                    ? "Mensagem para convidar à avaliação"
                    : "Mensagem antes de abrir a agenda"
                }
                value={form.postChoicePrompt}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    postChoicePrompt:
                      e.target.value
                  }))
                }
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                placeholder={
                  form.serviceFlowMode ===
                  "evaluation"
                    ? "Ex: Para esse procedimento, começamos com uma avaliação gratuita para entender o seu caso. Quer que eu veja os horários disponíveis?"
                    : "Opcional. Ex: Perfeito. Quer que eu veja os horários disponíveis?"
                }
                helperText={
                  form.serviceFlowMode ===
                  "evaluation"
                    ? "Pode informar se a avaliação é gratuita, presencial ou qualquer orientação específica da empresa."
                    : "Se ficar vazio, a Korax usa a mensagem padrão de agendamento."
                }
              />
            </Grid>

            <Grid item xs={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Switch checked={form.active} onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))} color="primary" size="small" />
                <Typography variant="body2">Serviço ativo</Typography>
              </div>
            </Grid>
          </Grid>
          {professionals.length > 0 && (
            <div className={classes.modalSection}>
              <Divider style={{ marginBottom: 16 }} />
              <Typography className={classes.sectionLabel}>
                <PeopleIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                Profissionais que realizam este serviço
              </Typography>
              <Grid container spacing={1}>
                {professionals.map(prof => {
                  const selected = selectedProfIds.includes(prof.id);
                  return (
                    <Grid item xs={12} sm={6} key={prof.id}>
                      <div className={`${classes.profCheckCard} ${selected ? classes.profCheckCardSelected : ""}`} onClick={() => toggleProfessional(prof.id)}>
                        <Avatar className={classes.profAvatar}>{prof.name?.charAt(0).toUpperCase()}</Avatar>
                        <Typography className={classes.profCheckName}>{prof.name}</Typography>
                        <Switch checked={selected} color="primary" size="small" onClick={e => e.stopPropagation()} onChange={() => toggleProfessional(prof.id)} />
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" style={{ borderRadius: 10, fontWeight: 700 }}>
            {editingService ? "Salvar alterações" : "Criar serviço"}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.topBar}>
        <div>
          <Typography className={classes.title}>Serviços</Typography>
          <Typography variant="caption" color="textSecondary">{services.length} serviço{services.length !== 1 ? "s" : ""} cadastrado{services.length !== 1 ? "s" : ""}</Typography>
        </div>
        <Button variant="contained" className={classes.addBtn} startIcon={<AddIcon style={{ fontSize: 16 }} />} onClick={() => handleOpenModal()}>
          Novo serviço
        </Button>
      </div>

      {services.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body2">Nenhum serviço cadastrado ainda.</Typography>
          <Typography variant="caption" color="textSecondary">Clique em "Novo serviço" para começar.</Typography>
        </div>
      ) : (
        <Grid container spacing={2}>
          {services.map(service => {
            const profIds = serviceProfMap[service.id] || [];
            return (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <div className={classes.serviceCard}>
                  <div className={classes.cardHeader}>
                    <Typography className={classes.cardName}>{service.name}</Typography>
                    <div className={classes.cardActions}>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpenModal(service)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => { setDeletingService(service); setConfirmOpen(true); }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </div>
                  </div>
                  <Typography className={classes.cardDesc}>{service.description || "Sem descrição"}</Typography>
                  <div className={classes.cardMeta}>
                    <span className={classes.metaItem}><AccessTimeIcon className={classes.metaIcon} />{service.durationMinutes} min</span>
                    {catalogByService[service.id]?.showPrice !== false && Number(service.price || 0) > 0 && (
                      <span className={classes.metaItem}>
                        <AttachMoneyIcon className={classes.metaIcon} />
                        R$ {Number(service.price).toFixed(2)}
                      </span>
                    )}
                    {catalogByService[service.id]?.serviceFlowMode === "evaluation" && (
                      <Chip
                        size="small"
                        label="Avaliação"
                        className={classes.statusChip}
                        style={{
                          background: "rgba(0,102,255,0.10)",
                          color: "#0066FF",
                          border: "1px solid rgba(0,102,255,0.25)"
                        }}
                      />
                    )}

                    <Chip size="small" label={service.active !== false ? "Ativo" : "Inativo"} className={classes.statusChip} style={{ background: service.active !== false ? "rgba(34,197,94,0.12)" : "rgba(156,163,175,0.12)", color: service.active !== false ? "#16a34a" : "#6b7280", border: `1px solid ${service.active !== false ? "rgba(34,197,94,0.3)" : "rgba(156,163,175,0.3)"}` }} />
                  </div>
                  {profIds.length > 0 && (
                    <div className={classes.professionalsRow}>
                      {profIds.map(id => (
                        <Tooltip key={id} title={getProfName(id)}>
                          <Avatar className={classes.profAvatar}>{getProfInitials(id)}</Avatar>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
};

export default ServicesManager;
